import React, { Component } from "react"
import { toast } from "react-toastify"

import { Firebase } from "../services/firebase"
import { initializeStripe } from "../services/stripeService"
import Profile from "../models/profile"

export const AppContext = React.createContext()

export class AppProvider extends Component {
  state = {
    findId: false,
    firebaseApp: false,
    stripe: false,
    isAuthenticated: false,
    profile: null,
    log: false,
    emailVerified: false,
    filters: [],
    heartDocs: false,
    lastDocs: [],
    currentPage: 0,
    unreadAlertsListener: () => {},
    profileListener: () => {},
    unreadAlerts: 0,
    updateFindId: findId => {
      this.setState({
        findId: findId,
      })
    },
    updateLog: log => {
      this.setState({
        log: log,
      })
    },
    updateEmailVerified: emailVerified => {
      this.setState({
        emailVerified: emailVerified,
      })
    },
    replaceHeartDocs: heartDocs => {
      this.setState({
        heartDocs: heartDocs,
      })
    },
    updateLastDocs: lastDoc => {
      this.setState({
        lastDocs: [...this.state.lastDocs, lastDoc],
      })
    },
    clearLastDocs: () => {
      this.setState({
        lastDocs: [],
      })
    },
    updateCurrentPage: page => {
      this.setState({
        currentPage: page,
      })
    },
    addFilters: filterToAdd => {
      const filters = [...this.state.filters, filterToAdd]

      // Using [...new Set(filters)] ensures that filters will be an array of unique values
      this.setState({
        filters: [...new Set(filters)],
      })
    },
    removeFilters: filterToRemove => {
      const filters = this.state.filters.filter(
        filter => filter !== filterToRemove
      )

      this.setState({
        filters: [...new Set(filters)],
      })
    },
    replaceFilters: filters => {
      this.setState({
        filters: filters,
      })
    },
    clearFilters: () => {
      this.setState({
        filters: [],
      })
    },
  }

  componentDidMount() {
    // Initialize the firebase app
    const firebaseApp = new Firebase()

    this.setState({
      firebaseApp: firebaseApp,
    })

    initializeStripe().then(stripe => {
      this.setState({
        stripe: stripe,
      })
    })

    // onAuthStateChanged, the user has been logged in and firebase app has been initialized.
    // Now safe to use firebaseApp properties to update state
    firebaseApp.auth.onAuthStateChanged(user => {
      if (user) {
        this.setState({
          isAuthenticated: true,
        })

        // Set emailVerified bool or send email verification if needed
        if (user.emailVerified) {
          this.setState({
            emailVerified: true,
          })
        } else {
          firebaseApp.auth.currentUser
            .sendEmailVerification()
            .then(() => {
              this.setState({
                emailVerified: false,
              })
            })
            .catch(err => {
              toast.error(`Sorry, an error occured. Error code 34. [${err}]`, {
                position: toast.POSITION.BOTTOM_CENTER,
                className: "has-text-white has-background-link",
                progressClassName: "link-progress-bar",
              })
            })
        }

        // If log exists, save to Firestore and clear it
        if (this.state.log) {
          const docRef = firebaseApp.db.collection("logs").doc()
          const log = this.state.log

          log.doc_id = firebaseApp.auth.currentUser.uid
          log.uid = firebaseApp.auth.currentUser.uid

          // For some background functions, unread alerts count is required.
          // Delay log addition for 3 seconds to ensure unread alerts are available.
          setTimeout(() => {
            docRef
              .set(log, { merge: true })
              .then(() => {
                this.setState({
                  log: false,
                })
              })
              .catch(err => {
                console.log("Error occurred while creating log.")
              })
          }, 3000)
        }

        const profileListener = firebaseApp.db
          .collection("profiles")
          .doc(firebaseApp.auth.currentUser.uid)
          .onSnapshot(
            snapshot => {
              const docData = snapshot.data()

              if (docData) {
                this.setState({
                  profile: new Profile(
                    docData["name"],
                    docData["payments_enabled"]
                  ),
                })
              }
            },
            err => {
              console.log("Unable to retrieve profile data at this time.")
            }
          )

        const unreadAlertsListener = firebaseApp.db
          .collection("unread_alerts")
          .doc(firebaseApp.auth.currentUser.uid)
          .onSnapshot(
            snapshot => {
              const docData = snapshot.data()

              if (docData && docData["count"]) {
                this.setState({
                  unreadAlerts: docData["count"],
                })
              } else {
                this.setState({
                  unreadAlerts: 0,
                })
              }
            },
            err => {
              console.log("Unable to get unread alerts at this time.")
            }
          )

        // Set listeners in context state so that they can be detached later if needed
        this.setState({
          unreadAlertsListener: unreadAlertsListener,
          profileListener: profileListener,
        })
      } else {
        // Invoke unreadAlertsListener to detach
        this.setState({
          isAuthenticated: false,
          profile: null,
          log: false,
          emailVerified: false,
          filters: [],
          heartDocs: false,
          lastDocs: [],
          currentPage: 0,
          unreadAlerts: 0,
          unreadAlertsListener: this.state.unreadAlertsListener(),
          profileListener: this.state.profileListener(),
        })
      }
    })
  }

  render() {
    return (
      <AppContext.Provider value={this.state}>
        {this.props.children}
      </AppContext.Provider>
    )
  }
}

export const AppConsumer = AppContext.Consumer
